/**
 * Юридические документы QA Studio · единый источник правды (29 JUN 2026).
 *
 * ВНИМАНИЕ (для команды, не для рендера):
 *  - Это рабочий шаблон, а НЕ юридическая консультация. Перед публикацией
 *    проверьте тексты у юриста под конкретную юрисдикцию.
 *  - Юрисдикции: оператор зарегистрирован в ЕС → базовый режим GDPR; аудитория
 *    РФ / Украина / Беларусь / ЕС. Тексты на русском.
 *  - ПЛЕЙСХОЛДЕРЫ в [КВАДРАТНЫХ СКОБКАХ] нужно заменить на реальные реквизиты.
 *    В UI они подсвечиваются жёлтым (<mark class="legal-ph">), чтобы их было
 *    видно. Один find-replace на токен заменяет все вхождения в этом файле.
 *  - Реквизиты заполнены (CZ · 29 JUN 2026): страна — Чешская Республика;
 *    адрес — Spojovací 24, 198 00 Praha; email — privacy@qastudio.app;
 *    надзорный орган — ÚOOÚ; сроки хранения — аналитика до 24 мес (технические логи как профиль не храним).
 *  - Оператор (OSVČ): Aliukha Yauheniya · IČO 08583773. Все реквизиты заполнены.
 *
 * Контент структурирован (без dangerouslySetInnerHTML). Рендерит LegalScreen.
 */

export type LegalDocId = 'terms' | 'privacy' | 'cookies' | 'consent';

export type LegalBlock =
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'table'; head: string[]; rows: string[][] }
  | { type: 'note'; text: string };

export interface LegalSection {
  id: string;
  heading: string;
  blocks: LegalBlock[];
}

export interface LegalDoc {
  id: LegalDocId;
  /** Сегмент хэш-маршрута: #/terms · #/privacy · #/cookies · #/consent */
  slug: LegalDocId;
  title: string;
  shortTitle: string;
  /** Дата последнего обновления (ISO), показывается под заголовком. */
  updated: string;
  /** Версия документа. */
  version: string;
  summary: string;
  sections: LegalSection[];
}

export const LEGAL_UPDATED = '2026-06-30';
export const LEGAL_VERSION = '1.1';

/** Общий блок реквизитов оператора — переиспользуется во всех документах. */
const operatorBlock: LegalBlock = {
  type: 'ul',
  items: [
    'Оператор: Aliukha Yauheniya — индивидуальный предприниматель (OSVČ), зарегистрированный в Чешской Республике.',
    'Идентификационный номер (IČO): 08583773.',
    'Юридический адрес: Spojovací 24, 198 00 Praha, Чешская Республика.',
    'Электронная почта для обращений: privacy@qastudio.app.',
    'Сайт сервиса: qastudio.app.',
  ],
};

// ───────────────────────────────────────────────────────────────────────────
// 1. ПОЛЬЗОВАТЕЛЬСКОЕ СОГЛАШЕНИЕ
// ───────────────────────────────────────────────────────────────────────────

const terms: LegalDoc = {
  id: 'terms',
  slug: 'terms',
  title: 'Пользовательское соглашение',
  shortTitle: 'Соглашение',
  updated: LEGAL_UPDATED,
  version: LEGAL_VERSION,
  summary: 'Условия использования образовательного сервиса QA Studio.',
  sections: [
    {
      id: 'operator',
      heading: '1. Оператор сервиса',
      blocks: [
        { type: 'p', text: 'Сервис QA Studio (далее — «Сервис») предоставляется следующим лицом (далее — «Оператор»):' },
        operatorBlock,
      ],
    },
    {
      id: 'terms-general',
      heading: '2. Общие положения и термины',
      blocks: [
        {
          type: 'p',
          text: 'Настоящее Пользовательское соглашение (далее — «Соглашение») регулирует отношения между Оператором и пользователем по поводу использования Сервиса — онлайн-курса и платформы для обучения тестированию программного обеспечения, доступной на сайте qastudio.app.',
        },
        {
          type: 'ul',
          items: [
            '«Сервис» — сайт qastudio.app, его содержимое, учебные материалы, интерактивные задания и связанные функции.',
            '«Пользователь» — физическое лицо, использующее Сервис и принявшее условия Соглашения.',
            '«Аккаунт» — учётная запись Пользователя, дающая доступ к Сервису.',
            '«Контент Сервиса» — тексты уроков, задания, тесты, графика, код, дизайн и иные материалы, права на которые принадлежат Оператору.',
            '«Пользовательский контент» — материалы, которые Пользователь создаёт в Сервисе (решения домашних заданий, портфолио, заметки).',
          ],
        },
        {
          type: 'p',
          text: 'Используя Сервис или создавая Аккаунт, Пользователь подтверждает, что прочитал, понял и принимает условия Соглашения. Если Пользователь не согласен с условиями — использование Сервиса следует прекратить.',
        },
      ],
    },
    {
      id: 'terms-subject',
      heading: '3. Предмет соглашения',
      blocks: [
        {
          type: 'p',
          text: 'Оператор предоставляет Пользователю доступ к Сервису для самостоятельного обучения. Объём доступа зависит от выбранного формата: демонстрационная (бесплатная) часть либо полный курс по подписке или единовременной оплате.',
        },
        {
          type: 'p',
          text: 'Сервис носит образовательный характер. Оператор не гарантирует трудоустройства, прохождения собеседований, получения сертификата третьих сторон или иного конкретного результата — итог обучения зависит от усилий Пользователя.',
        },
      ],
    },
    {
      id: 'terms-account',
      heading: '4. Регистрация и аккаунт',
      blocks: [
        {
          type: 'ul',
          items: [
            'Доступ к Сервису может предоставляться по приглашению либо после оплаты — в зависимости от текущей модели распространения.',
            'Пользователь обязуется указывать достоверные данные и поддерживать их актуальность.',
            'Пользователь самостоятельно отвечает за сохранность пароля и за все действия, совершённые под его Аккаунтом.',
            'О любом несанкционированном доступе к Аккаунту следует незамедлительно сообщить Оператору по адресу privacy@qastudio.app.',
            'Один Аккаунт предназначен для одного физического лица. Передача доступа третьим лицам не допускается.',
          ],
        },
        {
          type: 'note',
          text: 'Сервис предназначен для лиц от 16 лет. Пользователи младше указанного возраста используют Сервис только с согласия родителя или законного представителя.',
        },
      ],
    },
    {
      id: 'terms-payment',
      heading: '5. Доступ, оплата и возврат',
      blocks: [
        {
          type: 'p',
          text: 'В настоящее время доступ к Сервису предоставляется по приглашению, демонстрационная часть — бесплатно. Если будут введены платные тарифы:',
        },
        {
          type: 'ul',
          items: [
            'стоимость, состав и условия тарифов будут указываться на сайте Сервиса на момент оформления;',
            'оплата будет проводиться через сторонних платёжных провайдеров; реквизиты банковских карт Оператору не передаются и им не хранятся;',
            'цены могут изменяться; изменение не затрагивает уже оплаченный период доступа.',
          ],
        },
        {
          type: 'p',
          text: 'Право на отказ для потребителей из ЕС: потребитель вправе отказаться от договора в течение 14 дней без объяснения причин. Сервис предоставляет цифровой контент: если Пользователь прямо просит начать предоставление доступа до истечения срока отказа и подтверждает, что осознаёт утрату права на отказ с момента начала предоставления, право на отказ прекращается (статья 16(m) Директивы 2011/83/ЕС). Для потребителей из РФ, Украины и Беларуси применяются гарантии, установленные местным законодательством о защите прав потребителей.',
        },
      ],
    },
    {
      id: 'terms-ip',
      heading: '6. Интеллектуальная собственность',
      blocks: [
        {
          type: 'p',
          text: 'Все права на Контент Сервиса принадлежат Оператору или используются им на законном основании. Пользователю предоставляется ограниченная, неисключительная, непередаваемая лицензия на доступ к Контенту исключительно для личного некоммерческого обучения.',
        },
        {
          type: 'p',
          text: 'Запрещается без письменного согласия Оператора: копировать, тиражировать, публиковать, распространять, перепродавать, публично демонстрировать материалы Сервиса, а также создавать на их основе производные продукты.',
        },
      ],
    },
    {
      id: 'terms-ugc',
      heading: '7. Пользовательский контент',
      blocks: [
        {
          type: 'p',
          text: 'Права на Пользовательский контент остаются за Пользователем. Создавая такой контент в Сервисе, Пользователь предоставляет Оператору ограниченную лицензию на его хранение и обработку в объёме, необходимом для работы Сервиса (отображение прогресса, сохранение портфолио, проверка заданий).',
        },
        {
          type: 'p',
          text: 'Пользователь отвечает за то, что его контент не нарушает прав третьих лиц и требований закона.',
        },
      ],
    },
    {
      id: 'terms-rules',
      heading: '8. Правила использования',
      blocks: [
        { type: 'p', text: 'При использовании Сервиса запрещается:' },
        {
          type: 'ul',
          items: [
            'нарушать работу Сервиса, предпринимать попытки несанкционированного доступа, взлома, обхода ограничений и систем защиты;',
            'осуществлять автоматизированный сбор данных (скрейпинг), массовую выгрузку материалов, нагрузочные и иные атаки;',
            'использовать Сервис для незаконной деятельности, распространения вредоносного кода, нарушения прав третьих лиц;',
            'выдавать себя за другое лицо, передавать доступ к Аккаунту, создавать множественные аккаунты в обход ограничений;',
            'копировать и распространять материалы Сервиса в нарушение раздела об интеллектуальной собственности.',
          ],
        },
      ],
    },
    {
      id: 'terms-availability',
      heading: '9. Доступность и изменения Сервиса',
      blocks: [
        {
          type: 'p',
          text: 'Сервис предоставляется по принципу «как есть» (as is) и «как доступно» (as available). Оператор стремится обеспечить стабильную работу, но не гарантирует бесперебойного доступа и вправе проводить технические работы, обновлять, изменять или приостанавливать отдельные функции.',
        },
        {
          type: 'p',
          text: 'Отдельные функции могут предоставляться в экспериментальном (бета) режиме и изменяться без предварительного уведомления.',
        },
      ],
    },
    {
      id: 'terms-liability',
      heading: '10. Ответственность и гарантии',
      blocks: [
        {
          type: 'p',
          text: 'В пределах, допустимых применимым правом, Оператор не несёт ответственности за косвенные убытки, упущенную выгоду, потерю данных и иные последствия, возникшие в связи с использованием или невозможностью использования Сервиса.',
        },
        {
          type: 'note',
          text: 'Настоящий раздел не ограничивает права потребителей, предоставленные им императивными нормами законодательства страны их проживания (в том числе ЕС, РФ, Украины, Беларуси), и не исключает ответственность, которую нельзя исключить по закону.',
        },
      ],
    },
    {
      id: 'terms-termination',
      heading: '11. Приостановка и расторжение',
      blocks: [
        {
          type: 'ul',
          items: [
            'Оператор вправе ограничить или заблокировать доступ к Аккаунту при нарушении Соглашения, при злоупотреблениях или по требованию закона.',
            'Пользователь вправе в любой момент прекратить использование Сервиса и запросить удаление Аккаунта, написав на privacy@qastudio.app.',
            'После расторжения доступ к платному Контенту прекращается; положения об интеллектуальной собственности и ответственности сохраняют силу.',
          ],
        },
      ],
    },
    {
      id: 'terms-data',
      heading: '12. Персональные данные',
      blocks: [
        {
          type: 'p',
          text: 'Обработка персональных данных Пользователя осуществляется в соответствии с Политикой конфиденциальности и Политикой использования cookie, которые являются неотъемлемой частью настоящего Соглашения.',
        },
      ],
    },
    {
      id: 'terms-changes',
      heading: '13. Изменения соглашения',
      blocks: [
        {
          type: 'p',
          text: 'Оператор вправе изменять Соглашение. Актуальная редакция публикуется на этой странице с указанием даты обновления. При существенных изменениях Оператор уведомляет Пользователей дополнительно (например, по электронной почте или уведомлением в Сервисе). Продолжение использования Сервиса после вступления изменений в силу означает согласие с новой редакцией.',
        },
      ],
    },
    {
      id: 'terms-law',
      heading: '14. Применимое право и разрешение споров',
      blocks: [
        {
          type: 'p',
          text: 'К Соглашению применяется право Чешской Республики, если иное не предусмотрено императивными нормами. Для потребителей сохраняется защита, предоставляемая правом страны их обычного места жительства, и право обращаться в суд по месту своего жительства в соответствии с применимым законодательством.',
        },
        {
          type: 'p',
          text: 'Стороны стремятся разрешать споры путём переговоров. Если урегулировать спор не удаётся, потребитель из ЕС вправе обратиться в компетентный орган альтернативного разрешения споров (ADR) своей страны или в Европейский потребительский центр (ECC-Net), а также в суд по месту своего жительства.',
        },
      ],
    },
    {
      id: 'terms-contacts',
      heading: '15. Контакты',
      blocks: [
        { type: 'p', text: 'По вопросам, связанным с Соглашением, обращайтесь к Оператору:' },
        { type: 'ul', items: ['Aliukha Yauheniya', 'Spojovací 24, 198 00 Praha, Чешская Республика', 'privacy@qastudio.app'] },
      ],
    },
  ],
};

// ───────────────────────────────────────────────────────────────────────────
// 2. ПОЛИТИКА КОНФИДЕНЦИАЛЬНОСТИ (GDPR)
// ───────────────────────────────────────────────────────────────────────────

const privacy: LegalDoc = {
  id: 'privacy',
  slug: 'privacy',
  title: 'Политика конфиденциальности',
  shortTitle: 'Конфиденциальность',
  updated: LEGAL_UPDATED,
  version: LEGAL_VERSION,
  summary: 'Как мы обрабатываем и защищаем ваши персональные данные.',
  sections: [
    {
      id: 'privacy-controller',
      heading: '1. Кто обрабатывает ваши данные',
      blocks: [
        {
          type: 'p',
          text: 'Оператором (контролёром данных в смысле GDPR) персональных данных, обрабатываемых в Сервисе QA Studio, является:',
        },
        operatorBlock,
        {
          type: 'p',
          text: 'Поскольку Оператор зарегистрирован в ЕС, базовым стандартом обработки является Общий регламент по защите данных (GDPR, Регламент (ЕС) 2016/679). Для пользователей из РФ, Украины и Беларуси дополнительно учитываются требования местного законодательства (152-ФЗ «О персональных данных» в РФ; Закон Украины «Про захист персональних даних»; Закон Республики Беларусь «О защите персональных данных»).',
        },
      ],
    },
    {
      id: 'privacy-data',
      heading: '2. Какие данные мы собираем',
      blocks: [
        {
          type: 'p',
          text: 'Мы придерживаемся принципа минимизации и собираем только то, что необходимо для работы Сервиса:',
        },
        {
          type: 'ul',
          items: [
            'Адрес электронной почты — для входа, идентификации аккаунта и системных писем.',
            'Отображаемое имя — если вы его указали; может быть неполным (например, только имя). Поле необязательное.',
            'Данные обучения — ваш прогресс по спринтам и заданиям, результаты тестов, статистика практики, а также созданный вами учебный контент (решения заданий, портфолио, заметки).',
            'Пароль — хранится только в виде хэша и используется исключительно для аутентификации.',
            'Данные использования (внутренняя аналитика) — обезличенные или сессионные сведения о том, какие экраны вы открываете и как перемещаетесь по Сервису. Собираются только при вашем согласии на аналитические cookie, используются исключительно нами для улучшения Сервиса, не передаются третьим лицам и не применяются для рекламы или отслеживания на других сайтах.',
          ],
        },
        {
          type: 'p',
          text: 'Мы не собираем рекламные профили, не отслеживаем вас на других сайтах и не запрашиваем данные сверх перечисленного. Техническую информацию о подключении (например, IP-адрес) могут кратковременно обрабатывать наши инфраструктурные поставщики (хостинг, доставка контента) для предоставления и защиты Сервиса; мы не используем её для профилирования и не привязываем к вашему аккаунту. Записи о вашем согласии описаны в разделе «Сроки хранения».',
        },
      ],
    },
    {
      id: 'privacy-purposes',
      heading: '3. Цели и правовые основания обработки',
      blocks: [
        {
          type: 'table',
          head: ['Цель', 'Правовое основание (GDPR)'],
          rows: [
            ['Создание аккаунта и предоставление доступа к Сервису', 'Исполнение договора — ст. 6(1)(b)'],
            ['Сохранение прогресса обучения и оказание образовательной услуги', 'Исполнение договора — ст. 6(1)(b)'],
            ['Поддержка пользователей и коммуникация по работе Сервиса', 'Исполнение договора — ст. 6(1)(b)'],
            ['Безопасность и предотвращение злоупотреблений', 'Законный интерес — ст. 6(1)(f)'],
            ['Внутренняя продуктовая аналитика для улучшения Сервиса', 'Согласие — ст. 6(1)(a)'],
            ['Ведение журнала согласий и выполнение требований законодательства', 'Юридическая обязанность — ст. 6(1)(c)'],
          ],
        },
        {
          type: 'p',
          text: 'Если в отдельных случаях обработка основана на согласии, его можно отозвать в любой момент; это не влияет на законность обработки до отзыва.',
        },
      ],
    },
    {
      id: 'privacy-cookies',
      heading: '4. Cookie и аналогичные технологии',
      blocks: [
        {
          type: 'p',
          text: 'Сервис использует cookie и локальное хранилище браузера. Подробный перечень, назначение и способы управления описаны в Политике использования cookie. Необязательные категории включаются только после вашего согласия в баннере cookie.',
        },
      ],
    },
    {
      id: 'privacy-recipients',
      heading: '5. Кому мы передаём данные',
      blocks: [
        {
          type: 'p',
          text: 'Мы не продаём персональные данные. Для работы Сервиса данные обрабатываются проверенными поставщиками услуг (процессорами), действующими по нашему поручению на основании договоров об обработке данных (DPA):',
        },
        {
          type: 'table',
          head: ['Поставщик', 'Назначение'],
          rows: [
            ['Supabase', 'Хостинг базы данных и аутентификация'],
            ['Netlify', 'Хостинг сайта и доставка контента (CDN)'],
            ['Resend', 'Отправка системных писем (подтверждение регистрации, восстановление доступа)'],
          ],
        },
        {
          type: 'note',
          text: 'Перечень поставщиков поддерживается Оператором в актуальном состоянии; при подключении новых поставщиков он обновляется. Данные могут также раскрываться по обоснованному требованию государственных органов в рамках закона.',
        },
      ],
    },
    {
      id: 'privacy-transfer',
      heading: '6. Международная передача данных',
      blocks: [
        {
          type: 'p',
          text: 'Часть поставщиков может обрабатывать данные за пределами ЕЭЗ (в том числе в США). В таких случаях передача защищается предусмотренными GDPR механизмами: решениями Еврокомиссии об адекватности или стандартными договорными условиями (SCC) с дополнительными мерами защиты.',
        },
      ],
    },
    {
      id: 'privacy-retention',
      heading: '7. Сроки хранения',
      blocks: [
        {
          type: 'ul',
          items: [
            'Данные аккаунта и обучения хранятся, пока аккаунт активен, и в течение разумного срока после его удаления, необходимого для завершения обязательств.',
            'Записи о согласии (факт согласия, версия принятого документа, дата и время) хранятся и после удаления аккаунта — в течение срока, в который могут быть предъявлены или оспорены связанные требования, на основании ст. 17(3)(e) GDPR (защита правопритязаний).',
            'Данные внутренней аналитики хранятся в обезличенном или агрегированном виде ограниченный срок (до 24 месяцев).',
            'Документы, которые мы обязаны хранить по закону, хранятся в течение установленного законом срока.',
          ],
        },
        {
          type: 'p',
          text: 'После истечения сроков данные удаляются или обезличиваются.',
        },
      ],
    },
    {
      id: 'privacy-rights',
      heading: '8. Ваши права',
      blocks: [
        { type: 'p', text: 'В отношении своих персональных данных вы имеете право:' },
        {
          type: 'ul',
          items: [
            'получить доступ к данным и их копию (ст. 15 GDPR);',
            'исправить неточные данные (ст. 16);',
            'удалить данные — «право быть забытым» (ст. 17);',
            'ограничить обработку (ст. 18);',
            'на переносимость данных (ст. 20);',
            'возражать против обработки на основании законного интереса (ст. 21);',
            'отозвать ранее данное согласие в любой момент;',
            'не быть объектом решений, основанных исключительно на автоматизированной обработке и порождающих юридические последствия (ст. 22).',
          ],
        },
        {
          type: 'p',
          text: 'Чтобы воспользоваться правами, напишите на privacy@qastudio.app. Мы отвечаем в течение одного месяца; этот срок может быть продлён в сложных случаях с уведомлением.',
        },
      ],
    },
    {
      id: 'privacy-complaint',
      heading: '9. Право на жалобу',
      blocks: [
        {
          type: 'p',
          text: 'Если вы считаете, что обработка ваших данных нарушает закон, вы вправе подать жалобу в надзорный орган — по месту регистрации Оператора либо по своему месту жительства. Для пользователей из ЕС это Управление по защите персональных данных Чешской Республики (Úřad pro ochranu osobních údajů, ÚOOÚ). Пользователи из РФ могут обратиться в Роскомнадзор, из Украины — к Уполномоченному Верховной Рады по правам человека, из Беларуси — в Национальный центр защиты персональных данных.',
        },
      ],
    },
    {
      id: 'privacy-security',
      heading: '10. Безопасность',
      blocks: [
        {
          type: 'p',
          text: 'Мы применяем организационные и технические меры защиты: шифрование передачи данных (HTTPS), хранение паролей в виде хэшей, разграничение доступа, ограничение прав на уровне базы данных. Ни один способ передачи и хранения не является абсолютно безопасным, поэтому мы не можем гарантировать абсолютную защиту.',
        },
      ],
    },
    {
      id: 'privacy-children',
      heading: '11. Данные несовершеннолетних',
      blocks: [
        {
          type: 'p',
          text: 'Сервис не предназначен для детей младше 16 лет. Если такой пользователь предоставил данные без согласия родителя или законного представителя, сообщите нам на privacy@qastudio.app — мы удалим данные.',
        },
      ],
    },
    {
      id: 'privacy-automated',
      heading: '12. Автоматизированная обработка',
      blocks: [
        {
          type: 'p',
          text: 'Сервис может рассчитывать вспомогательные показатели обучения (например, ориентировочную готовность к экзамену) на основе вашего прогресса. Эти расчёты носят информационный характер, не порождают для вас юридических последствий и не заменяют ваших собственных решений.',
        },
      ],
    },
    {
      id: 'privacy-changes',
      heading: '13. Изменения политики',
      blocks: [
        {
          type: 'p',
          text: 'Мы можем обновлять настоящую Политику. Актуальная редакция с датой обновления публикуется на этой странице; о существенных изменениях мы уведомляем дополнительно.',
        },
      ],
    },
    {
      id: 'privacy-contacts',
      heading: '14. Контакты',
      blocks: [
        { type: 'p', text: 'По вопросам обработки персональных данных обращайтесь:' },
        { type: 'ul', items: ['Aliukha Yauheniya', 'Spojovací 24, 198 00 Praha, Чешская Республика', 'privacy@qastudio.app'] },
      ],
    },
  ],
};

// ───────────────────────────────────────────────────────────────────────────
// 3. ПОЛИТИКА ИСПОЛЬЗОВАНИЯ COOKIE
// ───────────────────────────────────────────────────────────────────────────

const cookies: LegalDoc = {
  id: 'cookies',
  slug: 'cookies',
  title: 'Политика использования cookie',
  shortTitle: 'Политика cookie',
  updated: LEGAL_UPDATED,
  version: LEGAL_VERSION,
  summary: 'Какие cookie и технологии хранения мы используем и как ими управлять.',
  sections: [
    {
      id: 'cookies-what',
      heading: '1. Что такое cookie и локальное хранилище',
      blocks: [
        {
          type: 'p',
          text: 'Cookie — небольшие файлы, которые сайт сохраняет в браузере. Помимо cookie, Сервис активно использует локальное хранилище браузера (localStorage и sessionStorage) — например, чтобы запоминать ваш прогресс по курсу на устройстве. Ниже все эти технологии вместе называются «технологиями хранения».',
        },
      ],
    },
    {
      id: 'cookies-categories',
      heading: '2. Категории',
      blocks: [
        {
          type: 'ul',
          items: [
            'Строго необходимые — обеспечивают вход, безопасность и базовую работу Сервиса. Работают всегда и не требуют согласия.',
            'Функциональные — запоминают ваши настройки (тема, язык) и прогресс обучения на устройстве.',
            'Аналитические — внутренняя аналитика Сервиса: помогает понять, как им пользуются, чтобы улучшать. Включаются только после вашего согласия; данные используются только нами, не передаются третьим лицам и не применяются для рекламы.',
          ],
        },
        {
          type: 'p',
          text: 'Рекламных и сторонних трекинговых технологий Сервис не использует.',
        },
      ],
    },
    {
      id: 'cookies-table',
      heading: '3. Какие технологии хранения мы используем',
      blocks: [
        {
          type: 'table',
          head: ['Идентификатор', 'Назначение', 'Категория', 'Тип / срок'],
          rows: [
            ['sb-*-auth-token', 'Сессия и аутентификация (Supabase)', 'Необходимые', 'localStorage / до выхода'],
            ['qastudio.v1.cookieConsent', 'Сохранение вашего выбора в баннере cookie', 'Необходимые', 'localStorage / постоянно'],
            ['qastudio.v1.last-screen, qastudio.v1.*', 'Прогресс, последний экран, настройки обучения', 'Функциональные', 'localStorage / постоянно'],
            ['qastudio.v1.analyticsSession', 'Идентификатор сессии для внутренней аналитики', 'Аналитические', 'sessionStorage / на время вкладки'],
            ['Тема и оформление', 'Запоминание выбранной темы и акцента', 'Функциональные', 'localStorage / постоянно'],
          ],
        },
        {
          type: 'note',
          text: 'Точный перечень технических идентификаторов может меняться с обновлениями Сервиса; категории и назначение остаются прежними.',
        },
      ],
    },
    {
      id: 'cookies-third',
      heading: '4. Сторонние сервисы',
      blocks: [
        {
          type: 'p',
          text: 'Инфраструктурные поставщики (Supabase, Netlify) могут устанавливать собственные технические cookie, необходимые для аутентификации и доставки контента. Подробнее — в Политике конфиденциальности.',
        },
      ],
    },
    {
      id: 'cookies-manage',
      heading: '5. Как управлять',
      blocks: [
        {
          type: 'ul',
          items: [
            'При первом визите баннер предлагает: «Принять все», «Только необходимые» или «Настроить».',
            'Изменить выбор можно в любой момент через ссылку «Настройки cookie» (в профиле и в подвале сайта).',
            'Технологии хранения можно также удалять и блокировать в настройках браузера. Отключение строго необходимых сделает работу Сервиса невозможной.',
          ],
        },
      ],
    },
    {
      id: 'cookies-consent',
      heading: '6. Согласие и его срок',
      blocks: [
        {
          type: 'p',
          text: 'Согласие на необязательные категории действует до его отзыва. Периодически (например, раз в 6–12 месяцев) баннер может запросить подтверждение выбора заново.',
        },
      ],
    },
    {
      id: 'cookies-changes',
      heading: '7. Изменения',
      blocks: [
        {
          type: 'p',
          text: 'Актуальная редакция Политики публикуется на этой странице с указанием даты обновления.',
        },
      ],
    },
  ],
};

// ───────────────────────────────────────────────────────────────────────────
// 4. СОГЛАСИЕ НА ОБРАБОТКУ ПЕРСОНАЛЬНЫХ ДАННЫХ
// ───────────────────────────────────────────────────────────────────────────

const consent: LegalDoc = {
  id: 'consent',
  slug: 'consent',
  title: 'Согласие на обработку персональных данных',
  shortTitle: 'Согласие на обработку',
  updated: LEGAL_UPDATED,
  version: LEGAL_VERSION,
  summary: 'Текст согласия, которое вы даёте при первом входе в Сервис.',
  sections: [
    {
      id: 'consent-intro',
      heading: '1. Предоставление согласия',
      blocks: [
        {
          type: 'p',
          text: 'Получая доступ к Сервису и отмечая соответствующий чекбокс при первом входе, вы (далее — «Субъект данных») свободно, своей волей и в своём интересе даёте согласие Оператору на обработку своих персональных данных на условиях ниже. Согласие действует как согласие в смысле ст. 6(1)(a) GDPR, а для пользователей из РФ — как согласие в смысле Федерального закона № 152-ФЗ «О персональных данных».',
        },
        operatorBlock,
      ],
    },
    {
      id: 'consent-data',
      heading: '2. Перечень персональных данных',
      blocks: [
        {
          type: 'ul',
          items: [
            'адрес электронной почты;',
            'отображаемое имя, если указано (может быть неполным);',
            'пароль (в хэшированном виде);',
            'данные о прогрессе обучения и созданный вами учебный контент (решения заданий, портфолио, заметки).',
          ],
        },
      ],
    },
    {
      id: 'consent-actions',
      heading: '3. Действия с данными и способы обработки',
      blocks: [
        {
          type: 'p',
          text: 'Согласие даётся на следующие действия: сбор, запись, систематизация, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передача (предоставление, доступ) поставщикам услуг, обезличивание, блокирование, удаление, уничтожение. Обработка осуществляется как с использованием средств автоматизации, так и без них.',
        },
      ],
    },
    {
      id: 'consent-purposes',
      heading: '4. Цели обработки',
      blocks: [
        {
          type: 'ul',
          items: [
            'регистрация и предоставление доступа к Сервису;',
            'оказание образовательных услуг и сохранение прогресса;',
            'поддержка пользователей и коммуникация по работе Сервиса;',
            'обеспечение безопасности и предотвращение злоупотреблений;',
            'исполнение договора и требований законодательства.',
          ],
        },
      ],
    },
    {
      id: 'consent-transfer',
      heading: '5. Передача и трансграничная обработка',
      blocks: [
        {
          type: 'p',
          text: 'Субъект данных соглашается на передачу своих данных поставщикам услуг Оператора, перечисленным в Политике конфиденциальности, в том числе на трансграничную передачу за пределы страны проживания и ЕЭЗ с применением предусмотренных законом мер защиты (SCC, решения об адекватности).',
        },
      ],
    },
    {
      id: 'consent-term',
      heading: '6. Срок действия и отзыв согласия',
      blocks: [
        {
          type: 'p',
          text: 'Согласие действует с момента его предоставления до его отзыва или до удаления аккаунта. Отозвать согласие можно в любой момент, направив обращение на privacy@qastudio.app. Отзыв согласия не влияет на законность обработки до отзыва. После отзыва дальнейшее использование Сервиса может стать невозможным, поскольку обработка указанных данных необходима для его работы.',
        },
      ],
    },
    {
      id: 'consent-ack',
      heading: '7. Подтверждение',
      blocks: [
        {
          type: 'p',
          text: 'Предоставляя согласие, вы подтверждаете, что ознакомлены с Пользовательским соглашением и Политикой конфиденциальности, что вам понятны цели и условия обработки, и что указанные вами данные являются достоверными.',
        },
      ],
    },
  ],
};

// ───────────────────────────────────────────────────────────────────────────

export const LEGAL_DOCS: LegalDoc[] = [terms, privacy, cookies, consent];

const BY_SLUG: Record<string, LegalDoc> = LEGAL_DOCS.reduce(
  (acc, d) => {
    acc[d.slug] = d;
    return acc;
  },
  {} as Record<string, LegalDoc>,
);

/** Найти документ по slug (`terms` | `privacy` | `cookies` | `consent`). */
export function getLegalDoc(slug: string): LegalDoc | undefined {
  return BY_SLUG[slug];
}

/**
 * Детерминированный отпечаток (FNV-1a 32-bit) текста документа — фиксируется в
 * записи согласия (consent_log.doc_hash), чтобы доказать, КАКОЙ именно текст
 * принял пользователь. Версия включена в отпечаток: смена `version` или любого
 * текста меняет хэш. Это фингерпринт целостности, не криптозащита.
 */
function fingerprintDoc(doc: LegalDoc): string {
  const text = JSON.stringify({
    v: doc.version,
    title: doc.title,
    summary: doc.summary,
    sections: doc.sections,
  });
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** Отпечаток текста каждого документа на текущую версию. */
export const LEGAL_DOC_HASHES: Record<LegalDocId, string> = {
  terms: fingerprintDoc(terms),
  privacy: fingerprintDoc(privacy),
  cookies: fingerprintDoc(cookies),
  consent: fingerprintDoc(consent),
};
